import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Globe, Search, MapPin, Star,
  CheckCircle, XCircle, AlertTriangle, ExternalLink, ChevronDown, ChevronUp,
  BarChart3, Award, Eye, Download, Send, Phone, Mail, User, DollarSign,
  Zap, Rocket, Server, FileText, PenTool, ArrowRightLeft,
  Clock, Smartphone, Users, Camera, Video, Share2, Tag, ShieldCheck, Square, CheckSquare,
} from 'lucide-react';
import api from '../services/api';
import Header from '../components/Header';

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface Scores {
  opportunity: number;
  seo: number;
  best_practices: number;
  gbp: number;
  page_speed: number;
  mobile: number;
}

interface Feature {
  feature: string;
  stat_text?: string;
  exact_quote?: string;
  source?: string;
  source_url?: string;
  impact_pct?: number;
  extra_stats?: Array<{ stat_text: string; exact_quote?: string; source: string; source_url: string }>;
}

interface GbpLocalPack {
  query: string;
  in_local_pack: boolean;
  local_pack_position?: number;
  local_pack_total?: number;
  local_pack_results?: Array<{
    position: number;
    name: string;
    rating?: number;
    review_count?: number;
    is_target?: boolean;
  }>;
}

interface GbpCitation {
  directory: string;
  found: boolean;
  phone_match?: boolean;
  address_match?: boolean;
}

interface ReportData {
  id: string;
  token: string;
  slug?: string;
  businessName: string;
  domain: string;
  city: string;
  niche: string;
  platform: string;
  scores: Scores;
  bestPracticesData: {
    features_found?: Feature[];
    features_missing?: Feature[];
    total_checks?: number;
  };
  seoData: Record<string, any>;
  gbpData: {
    scoring?: {
      overall_score?: number;
      grade?: string;
      category_scores?: Record<string, { score: number; max: number }>;
      recommendations?: Array<{
        title: string;
        description?: string;
        priority?: string;
        impact?: string;
        action?: string;
      }>;
      competitor_comparison?: Record<string, any>;
    };
    profile?: Record<string, any>;
    local_pack?: GbpLocalPack[];
    citations?: {
      directories_checked?: number;
      directories_found?: number;
      results?: GbpCitation[];
    };
  };
  googleRating: number;
  googleReviews: number;
  views: number;
  hasPdf?: boolean;
  createdAt: string;
  quickWins?: Array<{ title: string; body?: string; description?: string; impact?: string; score?: number }>;
  revenueData?: Record<string, any>;
  pricing?: Record<string, any>;
  weaknesses?: Array<{ label: string; detail?: string; score?: number }>;
  competitorData?: Record<string, any>;
  personalNote?: string;
  coreWebVitalsData?: Record<string, any>;
  accessibilityData?: Record<string, any>;
  contentQualityData?: Record<string, any>;
  websiteActive?: boolean | number;
  hasGbp?: boolean;
  hasWebsite?: boolean;
  relevantServices?: string[];
  contactInfo?: { name?: string; phone?: string; email?: string };
  industryStats?: Array<{
    stat: string;
    source: string;
    source_url?: string;
    impact_pct?: number;
    exact_quote?: string;
  }>;
  customFeatures?: Array<{
    name: string;
    description: string;
    tech?: string;
    why_it_matters?: string;
    source?: string;
    source_url?: string;
  }>;
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

function compositeGrade(score: number): { letter: string; color: string; label: string; bg: string } {
  if (score >= 80) return { letter: 'A', color: 'text-green-600', label: 'Excellent', bg: 'from-green-500 to-emerald-600' };
  if (score >= 60) return { letter: 'B', color: 'text-blue-600', label: 'Good', bg: 'from-blue-500 to-blue-600' };
  if (score >= 40) return { letter: 'C', color: 'text-yellow-600', label: 'Needs Work', bg: 'from-yellow-500 to-amber-500' };
  return { letter: 'D', color: 'text-red-600', label: 'Needs Attention', bg: 'from-red-500 to-red-600' };
}

function avg(...vals: (number | undefined | null)[]): number {
  const valid = vals.filter((v): v is number => v != null && v > 0);
  return valid.length ? Math.round(valid.reduce((a, b) => a + b, 0) / valid.length) : 0;
}

function speedLabel(ms: number): string {
  if (ms <= 2000) return 'Fast';
  if (ms <= 4000) return 'Moderate';
  return 'Slow';
}

function speedColor(ms: number): string {
  if (ms <= 2000) return 'text-green-600';
  if (ms <= 4000) return 'text-yellow-600';
  return 'text-red-600';
}

/* ── Grade Card Component ──────────────────────────────────────────────────── */

const GradeCard: React.FC<{
  letter: string; bg: string; label: string; title: string; subtitle: string;
}> = ({ letter, bg, label, title, subtitle }) => (
  <div className="flex flex-col items-center">
    <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br ${bg} flex items-center justify-center shadow-lg`}>
      <span className="text-5xl sm:text-6xl font-black text-white drop-shadow-md">{letter}</span>
    </div>
    <div className="mt-3 text-center">
      <div className="text-sm font-bold text-white/90">{title}</div>
      <div className="text-xs text-white/60">{label} · {subtitle}</div>
    </div>
  </div>
);

/* ── Collapsible Section ───────────────────────────────────────────────────── */

const Section: React.FC<{
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, icon, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-6 transition-shadow hover:shadow-md">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

/* ── Intake Form Component ──────────────────────────────────────────────── */

const IntakeForm: React.FC<{ businessName: string; domain: string; token: string; showPrices?: boolean }> = ({
  businessName, domain, token, showPrices = false,
}) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: 'consultation', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) { setFormError('Name and email are required.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      await api.post('/intake', { ...form, reportToken: token, businessName, domain });
      setSubmitted(true);
    } catch {
      setFormError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div id="intake-form" className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center mb-10">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">We've received your inquiry and will reach out within 24 hours with your free consultation details.</p>
      </div>
    );
  }

  return (
    <div id="intake-form" className="bg-gradient-to-br from-blue-50 to-white rounded-3xl border-2 border-blue-200 shadow-lg p-8 sm:p-10 mb-10">
      <div className="text-center mb-8">
        <h3 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">Get Your Free Consultation</h3>
        <p className="text-gray-500">Tell us about your project and we'll respond within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-xl mx-auto">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="Your name"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="you@email.com"
                required
              />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          {showPrices && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Interested In</label>
            <select
              value={form.service}
              onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
            >
              <option value="starter">Starter Package</option>
              <option value="growth">Growth Package</option>
              <option value="consultation">Free Consultation First</option>
            </select>
          </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
          <textarea
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            placeholder="Tell us about your goals or any specific needs…"
          />
        </div>

        {formError && (
          <div className="text-red-600 text-sm text-center">{formError}</div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors text-lg"
        >
          <Send className="h-5 w-5" />
          {submitting ? 'Sending…' : 'Request Free Consultation'}
        </button>

        <p className="text-xs text-gray-400 text-center">No obligation. No credit card required. We'll follow up within 24 hours.</p>
      </form>
    </div>
  );
};

/* ── Main Page ─────────────────────────────────────────────────────────────── */

const WebsiteReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      if (!token) { setError('Invalid report link'); setLoading(false); return; }
      try {
        const res = await api.get(`reports/${token}`);
        if (res.data.status === 'success') {
          setReport(res.data.data);
        } else {
          setError('Report not found');
        }
      } catch {
        setError('Report not found or has been removed.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-500 text-lg">Loading your report…</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Report Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'This report may have been removed or the link is invalid.'}</p>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-semibold">
            ← Back to iModernize
          </Link>
        </div>
      </div>
    );
  }

  const { scores, bestPracticesData: bp, gbpData: gbp } = report;
  const featuresMissing = bp?.features_missing || [];
  const featuresFound = bp?.features_found || [];
  const websiteUnreachable = (bp as any)?.website_unreachable === true;
  const gbpLocalPack = gbp?.local_pack || [];
  const gbpCitations = gbp?.citations || {};
  const gbpProfile = gbp?.profile || {};
  const quickWins = report.quickWins || [];
  const competitors = report.competitorData || {};
  const compList = competitors.competitors || [];
  const seo = report.seoData || {};
  const seoOnPage = seo.on_page || {};
  const seoLocal = seo.local_seo || {};

  // Keyword gap data from competitor analysis
  const keywordGaps = (competitors as any).keyword_gaps || {};
  const kwGapList: Array<{ keyword: string; volume: number; cpc: number }> =
    (keywordGaps.gaps || []).filter((g: any) => g.volume >= 100);

  // Backlink data (lead's own + competitor comparison)
  const comparison = (competitors as any).comparison || {};
  const leadBacklinks = (competitors as any).lead_backlinks || {};
  const leadDomainMetrics = (competitors as any).lead_domain_metrics || {};

  // CRO data
  const croData = (report as any).croData || {};
  const croScore = croData.cro_score;
  const croFindings = croData.findings || [];
  const croMissing = croData.missing_elements || [];
  const croFound = croData.found_elements || [];
  const ctaAnalysis = croData.cta_analysis || {};
  const foldAnalysis = croData.fold_analysis || {};

  // Composite grades
  const visScore = avg(scores.seo, scores.gbp);
  const expScore = avg(scores.page_speed, scores.mobile, scores.best_practices);
  const repScore = avg(
    report.googleRating ? Math.round(report.googleRating * 20) : undefined,
    scores.gbp
  );
  const visGrade = compositeGrade(visScore);
  const expGrade = compositeGrade(expScore);
  const repGrade = compositeGrade(repScore);

  return (
    <>
      <Helmet>
        <title>Website Review: {report.businessName} | iModernize</title>
        <meta name="description" content={`Free website audit for ${report.businessName} — actionable insights to get more customers online.`} />
        <style>{`
          @media print {
            .print\\:hidden { display: none !important; }
            nav, footer, .print-hide { display: none !important; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        `}</style>
      </Helmet>

      <Header />
      <div className="min-h-screen bg-gray-50">
        {/* ═══════════════════════════════════════════════════════════════
            HERO — 3 Composite Grades
        ═══════════════════════════════════════════════════════════════ */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
          <div className="max-w-5xl mx-auto px-4 pt-28 pb-14 sm:pt-32 sm:pb-20">
            <div className="text-center mb-10">
              <p className="text-blue-300 text-sm font-semibold tracking-widest uppercase mb-3">
                {report.hasWebsite === false ? 'Free Business Review' : 'Free Website Review'}
              </p>
              <h1 className="text-4xl sm:text-5xl font-extrabold mb-3">{report.businessName}</h1>
              <p className="text-blue-200 text-lg">
                {report.domain && <><Globe className="inline h-4 w-4 mr-1" />{report.domain}</>}
                {report.city && <> &nbsp;·&nbsp; <MapPin className="inline h-4 w-4 mr-1" />{report.city}</>}
              </p>
              {report.personalNote && (
                <div className="mt-4 max-w-xl mx-auto bg-white/10 rounded-xl px-5 py-3 text-sm text-blue-100 italic border border-white/10">
                  "{report.personalNote}"
                </div>
              )}
            </div>

            {/* 3 Grade Cards */}
            <div className="flex justify-center gap-6 sm:gap-10 mb-8">
              <GradeCard {...visGrade} title="Visibility" subtitle={`${visScore}/100`} />
              <GradeCard {...expGrade} title="Experience" subtitle={`${expScore}/100`} />
              <GradeCard {...repGrade} title="Reputation" subtitle={`${repScore}/100`} />
            </div>

            {/* Value Badge + Download */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-2 rounded-full font-extrabold text-sm shadow-lg">
                📊 Your Personalized Web Audit — Complimentary
              </div>
              {report.hasPdf && (
                <a
                  href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/reports/${token}/pdf`}
                  download
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-5 py-2 rounded-full font-semibold text-sm transition-colors print:hidden"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 -mt-8 relative z-10 pb-20">

          {/* ═══════════════════════════════════════════════════════════════
              1. CAN CUSTOMERS FIND YOU? (Visibility)
          ═══════════════════════════════════════════════════════════════ */}
          <Section
            title="Can Customers Find You?"
            subtitle="When someone searches for your services, here's where you show up"
            icon={<Search className="h-6 w-6 text-blue-600" />}
          >
            {/* SEO Basics */}
            {(seoOnPage.title || seoOnPage.meta_description != null || seoOnPage.h1_tags != null) && (
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 text-sm mb-3">SEO Basics</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Page Title', ok: !!seoOnPage.title, detail: seoOnPage.title ? `"${seoOnPage.title}"` : 'Not found — Google can\'t tell what your page is about' },
                    { label: 'Meta Description', ok: !!seoOnPage.meta_description, detail: seoOnPage.meta_description ? `Set (${seoOnPage.meta_description.length} chars)` : 'Not found — you\'re losing clicks in search results' },
                    { label: 'Header Tags (H1)', ok: (seoOnPage.h1_tags || 0) > 0, detail: seoOnPage.h1_tags ? `${seoOnPage.h1_tags} found` : 'None found — hurts SEO ranking' },
                    { label: 'SSL Certificate', ok: seoOnPage.has_ssl !== false, detail: seoOnPage.has_ssl !== false ? 'Secure ✓' : 'Not secure — browsers show warnings to visitors' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${item.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      {item.ok ? <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
                      <div>
                        <div className="font-semibold text-sm text-gray-800">{item.label}</div>
                        <div className="text-xs text-gray-500">{item.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Local SEO */}
            {seoLocal.geographic_terms && seoLocal.geographic_terms.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 text-sm mb-2">Local Search Reach</h3>
                <p className="text-sm text-gray-500 mb-3">Google found these location references on your site:</p>
                <div className="flex flex-wrap gap-2">
                  {seoLocal.geographic_terms.map((t: string, i: number) => (
                    <span key={i} className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full text-sm font-medium">
                      📍 {t}
                    </span>
                  ))}
                </div>
              </div>
            )}


            {/* Local Pack Rankings — the hero of visibility */}
            {gbpLocalPack.length > 0 && (
              <div className="mb-4">
                <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Google Maps Rankings
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  When customers search for your services, here's where you appear in Google Maps:
                </p>
                <div className="space-y-4">
                  {gbpLocalPack.filter(lp => lp.local_pack_results && lp.local_pack_results.length > 0).map((lp, i) => (
                    <div key={i} className={`rounded-xl border-l-4 p-4 ${
                      lp.in_local_pack ? 'border-l-green-500 bg-green-50/50' : 'border-l-red-400 bg-red-50/30'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800 text-sm">"{lp.query}"</span>
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                          lp.in_local_pack ? 'bg-green-600 text-white' : 'bg-red-500 text-white'
                        }`}>
                          {lp.in_local_pack ? `#${lp.local_pack_position}` : `NOT IN TOP ${lp.local_pack_total || 20}`}
                        </span>
                      </div>
                      {lp.local_pack_results && lp.local_pack_results.length > 0 && (
                        <LocalPackTable results={lp.local_pack_results} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* ═══════════════════════════════════════════════════════════════
              2. WHAT HAPPENS WHEN THEY VISIT? (Experience)
          ═══════════════════════════════════════════════════════════════ */}
          <Section
            title={report.hasWebsite === false ? "Why You Need a Website" : "What Happens When They Visit?"}
            subtitle={report.hasWebsite === false ? "What your business is missing without an online presence" : (report.websiteActive === false || report.websiteActive === 0) ? "Your website is currently down" : "Speed, mobile experience, and what we found"}
            icon={<Smartphone className="h-6 w-6 text-blue-600" />}
          >
            {report.hasWebsite === false ? (
              /* ── No-website path ──────────────────────────────────── */
              <div className="space-y-6">
                {/* Industry Stats — Why You Need a Website */}
                {(report.industryStats && report.industryStats.length > 0) && (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Here's what the data says about businesses like yours that don't have a website:
                    </p>
                    <div className="space-y-3">
                      {report.industryStats.map((s, i) => (
                        <div key={i} className="bg-amber-50 border border-amber-200 rounded-xl p-4 hover:border-amber-300 transition-colors">
                          <div className="flex items-start justify-between mb-1">
                            <span className="font-bold text-amber-900 text-sm flex-1">{s.stat}</span>
                            {s.impact_pct != null && s.impact_pct > 0 && (
                              <span className="bg-amber-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                {s.impact_pct}% impact
                              </span>
                            )}
                          </div>
                          {s.exact_quote && (
                            <p className="text-gray-600 text-sm italic leading-relaxed mt-1">"{s.exact_quote}"</p>
                          )}
                          {s.source_url ? (
                            <p className="text-xs text-gray-400 mt-1">
                              Source: <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{s.source} <ExternalLink className="inline h-3 w-3" /></a>
                            </p>
                          ) : s.source ? (
                            <p className="text-xs text-gray-400 mt-1">Source: {s.source}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Features — What We Would Build */}
                {(report.customFeatures && report.customFeatures.length > 0) && (
                  <div>
                    <h3 className="font-bold text-gray-800 text-lg mb-1 flex items-center gap-2">
                      <Rocket className="h-5 w-5 text-blue-600" /> What We Would Build for You
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      A modern, mobile-friendly website tailored to your business — here's what it would include:
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {report.customFeatures.map((f, i) => (
                        <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                          <div className="font-bold text-gray-900 text-sm mb-1">{f.name}</div>
                          <p className="text-xs text-gray-600 leading-relaxed">{f.description}</p>
                          {f.why_it_matters && (
                            <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                              <strong className="text-gray-600">Why it matters:</strong> {f.why_it_matters}
                            </p>
                          )}
                          {f.source_url ? (
                            <p className="text-xs text-gray-400 mt-1">
                              <a href={f.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{f.source} <ExternalLink className="inline h-3 w-3" /></a>
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fallback if no stats/features available */}
                {(!report.industryStats || report.industryStats.length === 0) && (!report.customFeatures || report.customFeatures.length === 0) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <div className="font-bold text-amber-800 text-base mb-2">Your Business Doesn't Have a Website</div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">
                      Without a website, potential customers searching for "{report.niche} in {report.city}" can't find you online.
                      A modern website would help you:
                    </p>
                    <div className="space-y-1 text-sm text-gray-700">
                      <div>✅ <strong>Capture leads 24/7</strong> — contact forms and click-to-call work while you sleep</div>
                      <div>✅ <strong>Build instant credibility</strong> — photos of your work, reviews, and service details</div>
                      <div>✅ <strong>Rank in Google</strong> — appear when customers search for "{report.niche} in {report.city}"</div>
                      <div>✅ <strong>Outperform competitors</strong> — a fast, mobile-friendly site converts visitors into customers</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (report.websiteActive === false || report.websiteActive === 0) ? (
              /* ── Website-down path ─────────────────────────────────── */
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-300 rounded-xl p-5">
                  <div className="font-bold text-red-800 text-base mb-2">⚠️ Your Website Is Down</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    We found that <strong>{report.domain}</strong> has a registered domain, but the website is
                    currently unreachable or inactive. When a potential customer clicks your link, they see an
                    error page — and immediately go to a competitor.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5 border-l-4 border-l-red-500">
                  <div className="font-bold text-gray-800 text-sm mb-1">Customers Read Reviews — Then Visit Your Site</div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    54% of consumers visit a business's website after reading positive reviews. Without a working website, those potential customers have nowhere to go.
                  </p>
                  <p className="text-xs text-gray-400 mt-2 italic">— <a href="https://www.brightlocal.com/research/local-consumer-review-survey/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">BrightLocal, Local Consumer Review Survey (2026)</a></p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5 border-l-4 border-l-amber-500">
                  <div className="font-bold text-gray-800 text-sm mb-1">Nearly Half of Google Searches Are Local</div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    46% of all Google searches are seeking local information. When someone searches for a service like yours, the first thing they expect to find is a working website with contact info.
                  </p>
                  <p className="text-xs text-gray-400 mt-2 italic">— <a href="https://blog.hubspot.com/marketing/local-seo-stats" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">HubSpot, Local SEO Stats (citing GoGulf)</a></p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5 border-l-4 border-l-purple-500">
                  <div className="font-bold text-gray-800 text-sm mb-1">97% Check Reviews Before Choosing</div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    97% of consumers read reviews for local businesses before choosing one. A working website with reviews, service details, and contact information is how customers decide who to trust.
                  </p>
                  <p className="text-xs text-gray-400 mt-2 italic">— <a href="https://www.brightlocal.com/research/local-consumer-review-survey/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">BrightLocal, Local Consumer Review Survey (2026)</a></p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <div className="font-bold text-green-800 text-sm mb-2">What a Working Website Would Do for You</div>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div>✅ <strong>Capture leads 24/7</strong> — contact forms and click-to-call work while you sleep</div>
                    <div>✅ <strong>Build instant credibility</strong> — photos of your work, reviews, and service details</div>
                    <div>✅ <strong>Rank in Google</strong> — appear when customers search for "{report.niche} in {report.city}"</div>
                    <div>✅ <strong>Outperform competitors</strong> — a fast, mobile-friendly site converts visitors into customers</div>
                  </div>
                </div>
              </div>
            ) : (
            /* ── Normal website-active path ───────────────────────── */
            <>
            {/* Speed & Mobile */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {scores.page_speed > 0 && (
                <div className={`rounded-xl border p-5 ${scores.page_speed >= 70 ? 'bg-green-50 border-green-200' : scores.page_speed >= 40 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="h-5 w-5 text-gray-600" />
                    <span className="font-bold text-gray-800">Desktop Speed</span>
                  </div>
                  <div className={`text-3xl font-extrabold ${scores.page_speed >= 70 ? 'text-green-600' : scores.page_speed >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {scores.page_speed}/100
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {scores.page_speed >= 70 ? 'Good — your site loads quickly for visitors'
                      : scores.page_speed >= 40 ? 'Could be faster — slow sites lose visitors'
                      : '53% of visitors leave if a page takes over 3 seconds to load (Google)'}
                  </p>
                </div>
              )}
              {scores.mobile > 0 && (
                <div className={`rounded-xl border p-5 ${scores.mobile >= 70 ? 'bg-green-50 border-green-200' : scores.mobile >= 40 ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Smartphone className="h-5 w-5 text-gray-600" />
                    <span className="font-bold text-gray-800">Mobile Experience</span>
                  </div>
                  <div className={`text-3xl font-extrabold ${scores.mobile >= 70 ? 'text-green-600' : scores.mobile >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {scores.mobile}/100
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {scores.mobile >= 70 ? 'Great — your site works well on phones'
                      : scores.mobile >= 40 ? 'Needs improvement — most of your customers browse on phones'
                      : '60%+ of searches are on mobile — your site needs work here'}
                  </p>
                </div>
              )}
            </div>

            {/* Missing Features */}
            {featuresMissing.length > 0 && !websiteUnreachable && (
              <div className="mb-4">
                <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  We Couldn't Find These on Your Website ({featuresMissing.length})
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  These features are standard for modern websites and help convert visitors into customers:
                </p>
                <div className="space-y-3">
                  {featuresMissing.map((f, i) => (
                    <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-4 hover:border-red-300 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-bold text-red-800 text-sm">{f.feature}</span>
                        {f.impact_pct && (
                          <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-2 shrink-0">
                            {f.impact_pct}% impact
                          </span>
                        )}
                      </div>
                      {f.exact_quote ? (
                        <p className="text-gray-600 text-sm italic leading-relaxed">"{f.exact_quote}"</p>
                      ) : f.stat_text ? (
                        <p className="text-gray-600 text-sm italic leading-relaxed">"{f.stat_text}"</p>
                      ) : null}
                      {f.source_url ? (
                        <p className="text-xs text-gray-400 mt-1">
                          Source: <a href={f.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{f.source} <ExternalLink className="inline h-3 w-3" /></a>
                        </p>
                      ) : f.source ? (
                        <p className="text-xs text-gray-400 mt-1">Source: {f.source}</p>
                      ) : null}
                      {f.extra_stats && f.extra_stats.length > 0 && (
                        <div className="mt-2 space-y-1 border-t border-red-200 pt-2">
                          {f.extra_stats.filter(e => e.exact_quote || e.stat_text).map((e, j) => (
                            <div key={j}>
                              <p className="text-gray-500 text-xs italic">"{e.exact_quote || e.stat_text}"</p>
                              {e.source_url ? (
                                <p className="text-xs text-gray-400">
                                  Source: <a href={e.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{e.source} <ExternalLink className="inline h-3 w-3" /></a>
                                </p>
                              ) : e.source ? (
                                <p className="text-xs text-gray-400">Source: {e.source}</p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features Present */}
            {featuresFound.length > 0 && (
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" /> What's Working ({featuresFound.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {featuresFound.map((f, i) => (
                    <span key={i} className="bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full text-sm font-medium">
                      ✓ {f.feature}
                    </span>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </Section>

          {/* ═══════════════════════════════════════════════════════════════
              3. HOW YOU COMPARE (Competitors)
          ═══════════════════════════════════════════════════════════════ */}
          {compList.length > 0 && (() => {
            // Determine which columns actually have data across all rows
            // Only show Rating/Reviews if at least one COMPETITOR has data (avoid columns of all dashes)
            const anyCompRating = compList.some((c: any) => c.rating != null && c.rating > 0);
            const anyCompReviews = compList.some((c: any) => c.review_count > 0);
            const hasRating = anyCompRating || (report.googleRating != null && report.googleRating > 0 && anyCompRating);
            const hasReviews = anyCompReviews || (report.googleReviews != null && report.googleReviews > 0 && anyCompReviews);
            const hasMobile = (!!scores.mobile && compList.some((c: any) => c.mobile_score)) || compList.filter((c: any) => c.mobile_score).length >= 2;
            const hasSpeed = (!!scores.page_speed && compList.some((c: any) => c.page_speed_score || c.speed_score)) || compList.filter((c: any) => c.page_speed_score || c.speed_score).length >= 2;
            const hasBacklinks = !!leadBacklinks.backlinks_total || compList.some((c: any) => c.backlinks_total);
            // Count how many data columns we actually have
            const dataColumnCount = [hasRating, hasReviews, hasMobile, hasSpeed, hasBacklinks].filter(Boolean).length;
            // Need at least one data column AND at least one competitor with any data to show the table
            const anyCompetitorHasData = compList.some((c: any) => 
              (c.rating != null && c.rating > 0) || c.review_count > 0 || c.mobile_score || c.page_speed_score || c.speed_score || c.backlinks_total
            );
            if (dataColumnCount === 0 || (!anyCompetitorHasData && dataColumnCount <= 1)) return null;
            return (
            <Section
              title="How You Compare"
              subtitle="Here's how your web presence stacks up against nearby competitors"
              icon={<Users className="h-6 w-6 text-blue-600" />}
              defaultOpen={true}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-gray-500 font-semibold">Business</th>
                      {hasRating && <th className="text-center py-2 px-3 text-gray-500 font-semibold">Rating</th>}
                      {hasReviews && <th className="text-center py-2 px-3 text-gray-500 font-semibold">Reviews</th>}
                      {hasMobile && <th className="text-center py-2 px-3 text-gray-500 font-semibold">Mobile</th>}
                      {hasSpeed && <th className="text-center py-2 px-3 text-gray-500 font-semibold">Speed</th>}
                      {hasBacklinks && <th className="text-center py-2 px-3 text-gray-500 font-semibold">Backlinks</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {/* You row */}
                    <tr className="bg-blue-50 font-bold border-b border-blue-200">
                      <td className="py-2 px-3 text-blue-700">{report.businessName} (You)</td>
                      {hasRating && <td className="py-2 px-3 text-center">{report.googleRating != null && report.googleRating > 0 ? `${report.googleRating.toFixed(1)} ★` : '—'}</td>}
                      {hasReviews && <td className="py-2 px-3 text-center">{report.googleReviews || '—'}</td>}
                      {hasMobile && <td className="py-2 px-3 text-center">{scores.mobile || '—'}</td>}
                      {hasSpeed && <td className="py-2 px-3 text-center">{scores.page_speed || '—'}</td>}
                      {hasBacklinks && <td className="py-2 px-3 text-center">{leadBacklinks.backlinks_total ? (leadBacklinks.backlinks_total).toLocaleString() : '—'}</td>}
                    </tr>
                    {compList.slice(0, 5).map((c: any, i: number) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-700">{c.name || c.domain}</td>
                        {hasRating && <td className="py-2 px-3 text-center text-gray-600">{c.rating != null && c.rating > 0 ? `${c.rating.toFixed(1)} ★` : '—'}</td>}
                        {hasReviews && <td className="py-2 px-3 text-center text-gray-600">{c.review_count || '—'}</td>}
                        {hasMobile && <td className="py-2 px-3 text-center text-gray-600">{c.mobile_score ?? c.page_speed_score ?? '—'}</td>}
                        {hasSpeed && <td className="py-2 px-3 text-center text-gray-600">{c.page_speed_score ?? c.speed_score ?? '—'}</td>}
                        {hasBacklinks && <td className="py-2 px-3 text-center text-gray-600">{c.backlinks_total ? c.backlinks_total.toLocaleString() : '—'}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {compList.length > 0 && (
                <p className="text-xs text-gray-400 mt-3 text-center">
                  {[hasRating || hasReviews ? 'Rating & reviews from Google Maps' : '', hasMobile || hasSpeed ? 'Speed & mobile from Google PageSpeed Insights' : '', hasBacklinks ? 'Backlinks from DataForSEO' : ''].filter(Boolean).join(' · ')}
                </p>
              )}
            </Section>
            );
          })()}

          {/* ═══════════════════════════════════════════════════════════════
              3b. KEYWORD INTELLIGENCE
          ═══════════════════════════════════════════════════════════════ */}
          {(kwGapList.length > 0 || comparison.avg_competitor_organic_keywords > 0 || (leadDomainMetrics.organic_keywords ?? 0) > 0) && (
            <Section
              title="Keyword Intelligence"
              subtitle={comparison.avg_competitor_organic_keywords > 0
                ? "How your organic keyword strength compares to competitors — and where the opportunities are"
                : "Your organic search visibility at a glance"}
              icon={<Search className="h-6 w-6 text-blue-600" />}
              defaultOpen={true}
            >
              {/* Competitor Keyword Advantage */}
              {comparison.avg_competitor_organic_keywords > 0 && (() => {
                const yourKw = leadDomainMetrics.organic_keywords ?? 0;
                const avgKw = comparison.avg_competitor_organic_keywords;
                const compsWithKw = compList.filter((c: any) => c.organic_keywords > 0);
                return (
                  <div className="mb-6">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-4">
                      <h3 className="font-bold text-gray-800 text-sm mb-3">🔍 Competitor Keyword Advantage</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        You rank for <span className="font-bold text-gray-800">{yourKw.toLocaleString()}</span> organic keywords.
                        Your competitors' typical count: <span className="font-bold text-gray-800">{avgKw.toLocaleString()}</span>
                        {yourKw >= avgKw && <> — <span className="font-bold text-green-600">you're ahead!</span></>}
                      </p>
                      {compsWithKw.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b-2 border-gray-200">
                                <th className="text-left py-2 px-3 text-gray-500 font-semibold">Business</th>
                                <th className="text-right py-2 px-3 text-gray-500 font-semibold">Organic Keywords</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-blue-50 font-bold border-b border-blue-200">
                                <td className="py-2 px-3 text-blue-700">{report.businessName} (You)</td>
                                <td className="py-2 px-3 text-right">{yourKw.toLocaleString()}</td>
                              </tr>
                              {compsWithKw.slice(0, 3).map((c: any, i: number) => (
                                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-2 px-3 text-gray-700">{c.name || c.domain}</td>
                                  <td className="py-2 px-3 text-right text-gray-600">{(c.organic_keywords || 0).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Standalone keyword info when no competitors */}
              {!(comparison.avg_competitor_organic_keywords > 0) && (leadDomainMetrics.organic_keywords ?? 0) > 0 && (
                <div className="mb-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                    <h3 className="font-bold text-gray-800 text-sm mb-3">Your Organic Presence</h3>
                    <p className="text-sm text-gray-600">
                      Your domain currently ranks for{' '}
                      <span className="font-bold text-gray-800">{(leadDomainMetrics.organic_keywords ?? 0).toLocaleString()}</span>{' '}
                      organic keywords, driving an estimated{' '}
                      <span className="font-bold text-gray-800">{(leadDomainMetrics.organic_traffic ?? 0).toLocaleString()}</span>{' '}
                      organic visits per month
                      {(leadDomainMetrics.organic_cost ?? 0) > 0 && (
                        <> (worth ~<span className="font-bold text-green-700">${Math.round(leadDomainMetrics.organic_cost).toLocaleString()}</span>/mo in paid ads)</>
                      )}.
                      {(leadDomainMetrics.organic_keywords ?? 0) < 50
                        ? ' There\'s significant room to grow your organic visibility with targeted local SEO.'
                        : ' A solid keyword foundation to build on.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Keyword Gaps */}
              {kwGapList.length > 0 && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-extrabold text-red-600">
                        {(keywordGaps.total_gap_volume || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">monthly searches you're invisible for</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                      <div className="text-3xl font-extrabold text-amber-600">
                        {kwGapList.length}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">high-value keywords with no ranking</div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-2 px-3 text-gray-500 font-semibold">Search Term</th>
                          <th className="text-right py-2 px-3 text-gray-500 font-semibold">Monthly Searches</th>
                          <th className="text-right py-2 px-3 text-gray-500 font-semibold">Avg. CPC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kwGapList.slice(0, 8).map((g: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 px-3 font-semibold text-gray-800">{g.keyword}</td>
                            <td className="py-2 px-3 text-right text-gray-600">{(g.volume || 0).toLocaleString()}</td>
                            <td className="py-2 px-3 text-right text-gray-600">${(g.cpc || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 mt-3 text-center">
                    National U.S. search volume from Google Ads via DataForSEO · CPC = cost per click if you ran paid ads for this term
                  </p>
                </>
              )}
            </Section>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              3c. BACKLINKS & AUTHORITY
          ═══════════════════════════════════════════════════════════════ */}
          {(leadBacklinks.backlinks_total > 0 || leadDomainMetrics.organic_keywords > 0 ||
            comparison.avg_competitor_backlinks > 0) && (
            <Section
              title="Backlinks & Authority"
              subtitle="How search engines measure your site's trustworthiness"
              icon={<Globe className="h-6 w-6 text-blue-600" />}
              defaultOpen={true}
            >
              <p className="text-sm text-gray-500 mb-4">
                Backlinks are links from other websites to yours. More quality backlinks = higher Google rankings.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                {leadBacklinks.backlinks_total != null && (
                  <div className={`rounded-xl border p-4 text-center ${
                    leadBacklinks.backlinks_total > 50 ? 'bg-green-50 border-green-200' :
                    leadBacklinks.backlinks_total > 10 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {(leadBacklinks.backlinks_total || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Total Backlinks</div>
                  </div>
                )}
                {leadBacklinks.referring_domains != null && (
                  <div className={`rounded-xl border p-4 text-center ${
                    leadBacklinks.referring_domains > 20 ? 'bg-green-50 border-green-200' :
                    leadBacklinks.referring_domains > 5 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {(leadBacklinks.referring_domains || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Referring Domains</div>
                  </div>
                )}
                {(leadDomainMetrics.organic_keywords != null || comparison.avg_competitor_organic_keywords > 0) && (
                  <div className={`rounded-xl border p-4 text-center ${
                    (leadDomainMetrics.organic_keywords ?? 0) > 100 ? 'bg-green-50 border-green-200' :
                    (leadDomainMetrics.organic_keywords ?? 0) > 20 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {(leadDomainMetrics.organic_keywords ?? 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Organic Keywords</div>
                  </div>
                )}
              </div>

              {/* Competitor comparison */}
              {comparison.avg_competitor_backlinks > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 text-sm mb-3">vs. Your Competitors</h4>
                  <div className="space-y-2 text-sm">
                    {comparison.avg_competitor_backlinks != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Typical Competitor Backlinks</span>
                        <span className="font-bold text-gray-800">{comparison.avg_competitor_backlinks.toLocaleString()}</span>
                      </div>
                    )}
                    {comparison.avg_competitor_referring_domains != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Typical Competitor Referring Domains</span>
                        <span className="font-bold text-gray-800">{comparison.avg_competitor_referring_domains.toLocaleString()}</span>
                      </div>
                    )}
                    {comparison.avg_competitor_organic_keywords != null && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Typical Competitor Organic Keywords</span>
                        <span className="font-bold text-gray-800">{comparison.avg_competitor_organic_keywords.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3 text-center">
                Authority metrics from DataForSEO backlink analysis
              </p>
            </Section>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              4. YOUR ONLINE REPUTATION
          ═══════════════════════════════════════════════════════════════ */}
          {(report.googleRating > 0 || gbpProfile.rating || (gbpCitations.results || []).length > 0) && (
            <Section
              title="Your Online Reputation"
              subtitle="Before a customer calls you, they look you up — here's what they see"
              icon={<Star className="h-6 w-6 text-blue-600" />}
            >
              {/* Star Rating Hero */}
              {(report.googleRating > 0) && (
                <div className="flex items-center gap-6 mb-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-center">
                    <div className="text-5xl font-extrabold text-gray-900">{report.googleRating?.toFixed(1)}</div>
                    <div className="flex gap-0.5 mt-1 justify-center">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`h-5 w-5 ${s <= Math.round(report.googleRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="text-gray-600">
                    <span className="text-2xl font-bold text-gray-900">{report.googleReviews}</span> reviews on Google
                    {report.googleReviews < 50 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Businesses with 50+ reviews get significantly more clicks than those with fewer
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* GBP Profile Checklist */}
              {gbpProfile.has_description != null && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 text-sm mb-3">Google Business Profile Completeness</h3>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      { label: 'Description', ok: gbpProfile.has_description, desc: 'Tells customers what you do' },
                      { label: 'Business Hours', ok: gbpProfile.has_hours, desc: 'Lets customers know when to call' },
                      { label: 'Photos', ok: (gbpProfile.photo_count || 0) >= 3, desc: `${gbpProfile.photo_count || 0} photos — businesses with 100+ get 520% more calls` },
                      { label: 'Google Posts', ok: (gbpProfile.posts_count || 0) > 0, desc: 'Regular posts keep your listing active' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${item.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        {item.ok ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                        <div>
                          <span className="font-semibold text-gray-800">{item.label}</span>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Citations */}
              {(gbpCitations.results || []).length > 0 && (
                <div>
                  <h3 className="font-bold text-gray-800 text-sm mb-3">
                    Directory Listings ({gbpCitations.directories_found ?? 0}/{gbpCitations.directories_checked ?? 0} found)
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(gbpCitations.results || []).map((c: GbpCitation, i: number) => {
                      let bg = 'bg-red-50 border-red-200';
                      let label = 'Not Listed';
                      if (c.found) {
                        const consistent = c.phone_match !== false && c.address_match !== false;
                        bg = consistent ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200';
                        label = consistent ? '✓ Listed' : '⚠ Info Mismatch';
                      }
                      return (
                        <div key={i} className={`rounded-lg border p-2 text-xs flex justify-between items-center ${bg}`}>
                          <span className="font-semibold text-gray-700">{c.directory}</span>
                          <span className="text-gray-500">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              4b. CONVERSION OPTIMIZATION (CRO)
          ═══════════════════════════════════════════════════════════════ */}
          {croScore != null && (
            <Section
              title="Conversion Optimization"
              subtitle="Is your website turning visitors into customers?"
              icon={<BarChart3 className="h-6 w-6 text-blue-600" />}
            >
              {/* CRO Score */}
              <div className="flex items-center gap-6 mb-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
                <div className="text-center">
                  <div className={`text-5xl font-extrabold ${
                    croScore >= 70 ? 'text-green-600' : croScore >= 40 ? 'text-amber-500' : 'text-red-600'
                  }`}>{croScore}</div>
                  <div className="text-xs text-gray-500 mt-1">CRO Score / 100</div>
                </div>
                <div className="flex-1 text-sm text-gray-600">
                  {croScore >= 70 ? 'Your website has solid conversion elements in place.' :
                   croScore >= 40 ? 'Your website has some conversion elements but is missing key pieces that cost you leads.' :
                   'Your website is missing critical conversion elements — visitors are leaving without contacting you.'}
                </div>
              </div>

              {/* Above-the-Fold Checklist */}
              <h4 className="font-bold text-gray-800 text-sm mb-3">Above the Fold (First Thing Visitors See)</h4>
              <div className="grid sm:grid-cols-2 gap-2 mb-5">
                {[
                  { label: 'Headline (H1)', ok: foldAnalysis.has_headline },
                  { label: 'Sub-headline', ok: foldAnalysis.has_subheadline },
                  { label: 'Call-to-Action Button', ok: foldAnalysis.has_cta },
                  { label: 'Hero Image', ok: foldAnalysis.has_image },
                ].map((item, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${
                    item.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    {item.ok ? '✅' : '❌'} {item.label}
                  </div>
                ))}
              </div>

              {/* CTA Analysis */}
              {ctaAnalysis.count != null && (
                <div className="mb-5">
                  <h4 className="font-bold text-gray-800 text-sm mb-2">Call-to-Action Buttons</h4>
                  <div className="text-sm text-gray-600 mb-2">Found {ctaAnalysis.count} CTA button{ctaAnalysis.count !== 1 ? 's' : ''} on your homepage.</div>
                  {ctaAnalysis.texts?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {ctaAnalysis.texts.slice(0, 5).map((t: string, i: number) => (
                        <span key={i} className="bg-blue-50 border border-blue-200 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">{t}</span>
                      ))}
                    </div>
                  )}
                  {ctaAnalysis.issues?.map((issue: string, i: number) => (
                    <div key={i} className="text-xs text-red-600 mt-1">⚠️ {issue}</div>
                  ))}
                </div>
              )}

              {/* Missing Elements */}
              {croMissing.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 text-sm mb-2">Missing Conversion Elements</h4>
                  <div className="space-y-2">
                    {croMissing.slice(0, 6).map((el: any, i: number) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <span className="text-red-500 mt-0.5">❌</span>
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">{el.name}</div>
                          {el.impact && <div className="text-xs text-gray-500 mt-0.5">{el.impact}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              5. WHAT TO DO NEXT (Action Plan)
          ═══════════════════════════════════════════════════════════════ */}
          {(() => {
            const siteDown = report.websiteActive === false || report.websiteActive === 0;
            const hasGbp = report.hasGbp === true || (report.googleRating != null && report.googleRating > 0) || (report.googleReviews != null && report.googleReviews > 0);
            // Filter out website-specific tips when site is down
            const SITE_KEYWORDS = ['your website', 'your site', 'on your site', 'homepage', 'landing page', 'page speed', 'meta description', 'title tag', 'schema markup', 'alt text', 'blog post'];
            // Filter out GBP-claim tips when business already has GBP
            const GBP_CLAIM_KEYWORDS = ['claim your google', 'claim and optimize google', 'claim and optimize your google', 'claim google my business', 'claim your gmb', 'set up a google business', 'create a google business', 'register on google'];
            let filteredTips = siteDown
              ? quickWins.filter((tip: any) => {
                  // High-priority tips (score >= 90) are always intentional — e.g. "Your Website Is Down"
                  if ((tip.score ?? 0) >= 90) return true;
                  const text = ((tip.title || '') + ' ' + (tip.body || tip.description || '')).toLowerCase();
                  return !SITE_KEYWORDS.some(kw => text.includes(kw));
                })
              : quickWins;
            if (hasGbp) {
              filteredTips = filteredTips.filter((tip: any) => {
                const text = ((tip.title || '') + ' ' + (tip.body || tip.description || '')).toLowerCase();
                return !GBP_CLAIM_KEYWORDS.some(kw => text.includes(kw));
              });
            }
            return filteredTips.length > 0 ? (
            <Section
              title="Your Top Opportunities"
              subtitle="Ranked by impact — these are the most important actions you can take right now"
              icon={<Zap className="h-6 w-6 text-blue-500" />}
            >
              <div className="space-y-3">
                {filteredTips.map((tip: any, i: number) => {
                  const score = tip.score ?? 50;
                  let badgeBg: string, badgeText: string, borderColor: string, leftBorder: string;
                  if (score >= 80) {
                    badgeBg = 'bg-red-600'; badgeText = 'HIGH PRIORITY';
                    borderColor = 'border-red-200'; leftBorder = 'border-l-red-600';
                  } else if (score >= 60) {
                    badgeBg = 'bg-amber-500'; badgeText = 'MEDIUM';
                    borderColor = 'border-amber-200'; leftBorder = 'border-l-amber-500';
                  } else {
                    badgeBg = 'bg-indigo-500'; badgeText = 'SUGGESTED';
                    borderColor = 'border-indigo-200'; leftBorder = 'border-l-indigo-500';
                  }
                  const bodyText = tip.body || tip.description;
                  return (
                    <div key={i} className={`flex gap-4 items-start p-4 bg-white border border-l-4 ${borderColor} ${leftBorder} rounded-lg shadow-sm`}>
                      <div className="flex-shrink-0 min-w-[28px] text-center">
                        <span className="text-xl font-black text-gray-900 leading-none">{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-gray-900 text-sm">{tip.title || tip}</span>
                          <span className={`text-white text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeBg} tracking-wide`}>{badgeText}</span>
                        </div>
                        {bodyText && <p className="text-xs text-gray-600 leading-relaxed">{bodyText}</p>}
                        {tip.impact && (
                          <p className="text-xs text-gray-500 mt-1.5 pt-1.5 border-t border-gray-100">
                            <strong className="text-gray-600">Why it matters:</strong> {tip.impact}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">
                Personalized based on your audit data — ranked by impact on your local search visibility
              </p>
            </Section>
            ) : null;
          })()}

          {/* ═══════════════════════════════════════════════════════════════
              6. SERVICE PACKAGES (Interactive Pricing Builder)
          ═══════════════════════════════════════════════════════════════ */}
          <PricingBuilder report={report} token={token || ''} />

          {/* ── Intake Form ───────────────────────────────────────────── */}
          <IntakeForm businessName={report.businessName} domain={report.domain} token={token || ''} showPrices={!!(report.pricing?.show_prices)} />

          {/* ── Contact CTA Bar ─────────────────────────────────────── */}
          {(report.contactInfo?.phone || report.contactInfo?.email) && (
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 sm:p-10 mb-8 text-center shadow-xl">
              <p className="text-lg text-gray-400 uppercase tracking-widest font-semibold mb-2">Prefer to talk directly?</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                Give {report.contactInfo.name || 'Us'} a Call or Send an Email
              </h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">We'll get back to you ASAP — no commitment, no pressure.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {report.contactInfo.phone && (
                  <a href={`tel:${report.contactInfo.phone}`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition-colors">
                    <Phone className="h-5 w-5" /> {report.contactInfo.phone}
                  </a>
                )}
                {report.contactInfo.email && (
                  <a href={`mailto:${report.contactInfo.email}`} className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-900 font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition-colors border border-gray-200">
                    <Mail className="h-5 w-5" /> {report.contactInfo.email}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* ── Footer ──────────────────────────────────────────────── */}
          <div className="text-center mt-10 text-sm text-gray-400">
            <p>
              This report was generated by{' '}
              <Link to="/" className="text-blue-500 hover:underline font-medium">iModernize</Link>.
              All statistics cited are from verified, independently verifiable sources.
            </p>
            {report.views > 1 && (
              <p className="mt-1 flex items-center justify-center gap-1">
                <Eye className="h-3 w-3" /> Viewed {report.views} times
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Sub-components ────────────────────────────────────────────────────────── */

const LocalPackTable: React.FC<{
  results: Array<{ position: number; name: string; rating?: number; review_count?: number; is_target?: boolean }>;
}> = ({ results }) => {
  const [expanded, setExpanded] = useState(false);
  // Filter out entries where the scrape failed (no name and no rating/reviews)
  const valid = results.filter(r => r.name?.trim() || (r.rating && r.rating > 0) || (r.review_count && r.review_count > 0));
  const shown = expanded ? valid : valid.slice(0, 5);
  return (
    <div>
      <div className="space-y-1.5 mt-3">
        {shown.map((r, j) => (
          <div key={j} className={`flex justify-between items-center text-sm px-4 py-2.5 rounded-lg ${
            r.is_target ? 'bg-green-100 font-bold text-green-800 border border-green-300' : 'bg-white/80 text-gray-700 border border-gray-100'
          }`}>
            <span className="font-medium">#{r.position} {r.name}{r.is_target ? ' ← YOU' : ''}</span>
            <span className="text-xs text-gray-500">{r.rating ?? 0} ★ · {r.review_count ?? 0} reviews</span>
          </div>
        ))}
      </div>
      {valid.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
        >
          {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Show all {valid.length} results</>}
        </button>
      )}
    </div>
  );
};

/* ── Pricing Builder Component ──────────────────────────────────────────── */

interface PricingItem {
  id: string;
  label: string;
  price: number;
  period: string;           // 'one-time' | '/mo' | '/post' | ''
  icon: React.ReactNode;
  desc: string;
  type: 'package' | 'addon';
  highlighted?: boolean;
}

const PricingBuilder: React.FC<{ report: ReportData; token: string }> = ({ report, token }) => {
  const p = report.pricing || {} as Record<string, any>;
  const showPrices = !!(p.show_prices);
  const coupon = Number(p.coupon_pct) || 0;
  const hasWebsite = report.hasWebsite !== false && report.websiteActive !== false && report.websiteActive !== 0;
  const [selected, setSelected] = useState<Set<string>>(new Set(['growth']));
  const relevantSvcs = new Set(report.relevantServices || ['autobooking','chatbot','loyalty','text_alerts','payment']);

  const toggle = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (id === 'starter' || id === 'growth') {
        next.delete('starter');
        next.delete('growth');
        if (!prev.has(id)) next.add(id);
      } else {
        next.has(id) ? next.delete(id) : next.add(id);
      }
      return next;
    });
  }, []);

  const allAddons: PricingItem[] = useMemo(() => {
    const addons: PricingItem[] = [];
    if (p.show_addon_migration !== false)
      addons.push({ id: 'migration', label: 'Hosting Migration', price: Number(p.addon_migration) || 100, period: 'one-time', icon: <ArrowRightLeft className="h-5 w-5 text-blue-500" />, desc: 'Zero-downtime migration to fast cloud hosting.', type: 'addon' });
    if (p.show_addon_blog !== false)
      addons.push({ id: 'blog', label: 'Blog Content', price: Number(p.addon_blog) || 75, period: '/post', icon: <PenTool className="h-5 w-5 text-purple-500" />, desc: 'SEO-optimized articles targeting local search terms.', type: 'addon' });
    if (p.show_addon_chatbot !== false)
      addons.push({ id: 'chatbot', label: 'AI Chatbot', price: Number(p.addon_chatbot) || 19, period: '/mo', icon: <FileText className="h-5 w-5 text-orange-500" />, desc: '24/7 AI chat that captures leads & books appointments.', type: 'addon' });
    if (p.show_addon_photography)
      addons.push({ id: 'photography', label: 'Photography', price: Number(p.addon_photography) || 0, period: '', icon: <Camera className="h-5 w-5 text-pink-500" />, desc: 'Professional photos of your business, team & work.', type: 'addon' });
    if (p.show_addon_video)
      addons.push({ id: 'video', label: 'Video & Editing', price: Number(p.addon_video) || 0, period: '', icon: <Video className="h-5 w-5 text-red-500" />, desc: 'Short promo videos, testimonials & social content.', type: 'addon' });
    if (p.show_addon_social)
      addons.push({ id: 'social', label: 'Social Media', price: Number(p.addon_social) || 0, period: '/mo', icon: <Share2 className="h-5 w-5 text-cyan-500" />, desc: 'Content calendars, posting & managed strategy.', type: 'addon' });
    return addons;
  }, [p]);

  const starterPrice = Number(p.starter) || 997;
  const growthPrice = Number(p.growth) || 1997;

  const applyDiscount = (n: number) => coupon > 0 ? Math.round(n * (1 - coupon / 100)) : n;

  const estimate = useMemo(() => {
    let oneTime = 0;
    let monthly = 0;
    if (selected.has('starter')) oneTime += starterPrice;
    if (selected.has('growth')) oneTime += growthPrice;
    for (const addon of allAddons) {
      if (!selected.has(addon.id)) continue;
      if (addon.period === '/mo') monthly += addon.price;
      else oneTime += addon.price;
    }
    return {
      oneTime,
      monthly,
      oneTimeDisc: applyDiscount(oneTime),
      monthlyDisc: applyDiscount(monthly),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, allAddons, starterPrice, growthPrice, coupon]);

  // ── No-prices mode: show clean services showcase (matches PDF) ───────────
  if (!showPrices) {
    type SvcDef = { id: string; label: string; desc: string; icon: React.ReactNode };
    const ALL_SERVICES: SvcDef[] = [
      { id: 'autobooking', label: 'Online Booking', desc: 'Customers book appointments 24/7 — syncs to your calendar.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg> },
      { id: 'chatbot', label: 'AI Chatbot', desc: '24/7 AI chat that captures leads and answers questions.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
      { id: 'loyalty', label: 'Loyalty Program', desc: 'Reward repeat customers with points and exclusive offers.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> },
      { id: 'text_alerts', label: 'Text Alerts', desc: 'SMS promotions, reminders, and updates sent to customers.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 10.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17.92z"/></svg> },
      { id: 'payment', label: 'Accept Payments', desc: 'Credit cards, invoices, and deposits collected through your site.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg> },
      { id: 'marketplace', label: 'Marketplace', desc: 'Sell products or services with a built-in storefront.', icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> },
    ];
    const nicheServices = ALL_SERVICES.filter(s => relevantSvcs.has(s.id));
    if (nicheServices.length === 0) return null;
    return (
      <Section
        title="Our Services"
        subtitle="We tailor everything to your business — here's what we can do for you"
        icon={<Rocket className="h-6 w-6 text-blue-600" />}
        defaultOpen={true}
      >
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div className="rounded-xl border border-gray-200 p-5" style={{ borderTop: '3px solid #3b82f6' }}>
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Website Redesign</h3>
            <ul className="text-sm text-gray-600 space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5 shrink-0">✓</span> Modern responsive design &amp; mobile optimization</li>
              <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5 shrink-0">✓</span> Contact form &amp; click-to-call button</li>
              <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5 shrink-0">✓</span> SEO setup &amp; Google Analytics</li>
              <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5 shrink-0">✓</span> Google Business Profile optimization</li>
              <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5 shrink-0">✓</span> Schema markup &amp; competitor strategy</li>
            </ul>
          </div>
          <div className="rounded-xl border border-gray-200 p-5" style={{ borderTop: '3px solid #16a34a' }}>
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Website Hosting</h3>
            <ul className="text-sm text-gray-600 space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-green-600 mt-0.5 shrink-0">✓</span> Fast cloud hosting &amp; SSL certificate</li>
              <li className="flex items-start gap-2"><span className="text-green-600 mt-0.5 shrink-0">✓</span> Daily backups &amp; uptime monitoring</li>
              <li className="flex items-start gap-2"><span className="text-green-600 mt-0.5 shrink-0">✓</span> Security updates included</li>
              <li className="flex items-start gap-2"><span className="text-green-600 mt-0.5 shrink-0">✓</span> More affordable than Wix or Squarespace</li>
            </ul>
          </div>
        </div>
        {nicheServices.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Add-on Services</p>
            <div className={`grid gap-3 ${nicheServices.length >= 3 ? 'sm:grid-cols-3' : nicheServices.length === 2 ? 'sm:grid-cols-2' : ''}`}>
              {nicheServices.map(svc => (
                <div key={svc.id} className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="shrink-0 mt-0.5 text-blue-500">{svc.icon}</div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{svc.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{svc.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>
    );
  }

  const PriceDisplay: React.FC<{ amount: number; size?: string }> = ({ amount, size = 'text-4xl' }) => {
    if (amount <= 0) return <span className={`${size} font-extrabold text-gray-900`}>Custom</span>;
    if (coupon > 0) {
      return (
        <span className="inline-flex items-baseline gap-2">
          <span className={`${size} font-extrabold text-red-600`}>${applyDiscount(amount).toLocaleString()}</span>
          <span className="text-lg text-gray-400 line-through">${amount.toLocaleString()}</span>
        </span>
      );
    }
    return <span className={`${size} font-extrabold text-gray-900`}>${amount.toLocaleString()}</span>;
  };

  const deposit = Math.round(estimate.oneTimeDisc * 0.2); // 20% deposit

  return (
    <div className="mt-12 mb-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">Let Us Fix This For You</h2>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
          Every issue in this report is solvable. Select a package and any add-ons — your estimate updates live.
        </p>
        {coupon > 0 && (
          <div className="inline-flex items-center gap-2 mt-3 bg-red-50 text-red-700 border border-red-200 px-4 py-2 rounded-full text-sm font-bold">
            <Tag className="h-4 w-4" /> {coupon}% OFF — Limited Time Sale
          </div>
        )}
      </div>

      {/* Package Cards */}
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {/* Starter */}
        <button
          onClick={() => toggle('starter')}
          className={`bg-white rounded-2xl p-6 text-center shadow-sm transition-all text-left ${
            selected.has('starter') ? 'border-2 border-blue-500 shadow-lg ring-2 ring-blue-100' : 'border border-gray-200 hover:shadow-lg hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-xl">
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
            {selected.has('starter')
              ? <CheckSquare className="h-6 w-6 text-blue-600" />
              : <Square className="h-6 w-6 text-gray-300" />
            }
          </div>
          <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Starter</div>
          <div className="mb-1"><PriceDisplay amount={starterPrice} /></div>
          <div className="text-sm text-gray-400 mb-4">one-time</div>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Modern responsive redesign</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Mobile optimization</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Contact form &amp; CTAs</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Basic SEO setup</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Google Analytics</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Hosting migration included</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> 30-day support</li>
          </ul>
        </button>

        {/* Growth */}
        <button
          onClick={() => toggle('growth')}
          className={`bg-white rounded-2xl p-6 text-center shadow-sm transition-all text-left relative ${
            selected.has('growth') ? 'border-2 border-blue-500 shadow-lg ring-2 ring-blue-100' : 'border-2 border-blue-200 hover:shadow-lg hover:border-blue-400'
          }`}
        >
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold uppercase tracking-wide px-4 py-1 rounded-full">
            Most Popular
          </div>
          <div className="flex items-center justify-between mb-3 mt-1">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 rounded-xl">
              <Rocket className="h-5 w-5 text-white" />
            </div>
            {selected.has('growth')
              ? <CheckSquare className="h-6 w-6 text-blue-600" />
              : <Square className="h-6 w-6 text-gray-300" />
            }
          </div>
          <div className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Growth</div>
          <div className="mb-1"><PriceDisplay amount={growthPrice} /></div>
          <div className="text-sm text-gray-400 mb-4">one-time</div>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Everything in Starter</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Full SEO optimization</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Google Business Profile setup</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Schema markup &amp; structured data</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> 6 FREE SEO blog posts (2/mo for 3 mo)</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> Content strategy &amp; keyword research</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" /> 90-day support &amp; training</li>
          </ul>
        </button>
      </div>

      {/* Competitive Hosting Note — only when they have an existing website */}
      {hasWebsite && (
        <div className="max-w-3xl mx-auto mt-6 bg-green-50 border border-green-200 rounded-xl px-5 py-3 flex items-start gap-3">
          <Server className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-green-800 text-sm">Competitive web hosting included.</span>{' '}
            <span className="text-green-700 text-sm">
              We offer fast cloud hosting with SSL, daily backups &amp; uptime monitoring — at rates that can most likely lower your existing hosting bill. Free migration with any package.
            </span>
          </div>
        </div>
      )}

      {/* Add-On Services */}
      {allAddons.length > 0 && (
        <div className="mt-8 max-w-3xl mx-auto">
          <h3 className="text-lg font-bold text-gray-700 text-center mb-4">Add-On Services</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allAddons.map(addon => {
              const isOn = selected.has(addon.id);
              return (
                <button
                  key={addon.id}
                  onClick={() => toggle(addon.id)}
                  className={`bg-white rounded-xl border p-5 text-left transition-all ${
                    isOn ? 'border-blue-400 shadow-md ring-2 ring-blue-100' : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>{addon.icon}</div>
                    {isOn
                      ? <CheckSquare className="h-5 w-5 text-blue-600" />
                      : <Square className="h-5 w-5 text-gray-300" />
                    }
                  </div>
                  <div className="font-bold text-gray-900 text-sm">{addon.label}</div>
                  <div className="mt-1">
                    {addon.price > 0 ? (
                      <>
                        {coupon > 0 ? (
                          <span className="inline-flex items-baseline gap-1.5">
                            <span className="text-xl font-extrabold text-red-600">${applyDiscount(addon.price)}</span>
                            <span className="text-sm text-gray-400 line-through">${addon.price}</span>
                          </span>
                        ) : (
                          <span className="text-xl font-extrabold text-gray-900">${addon.price}</span>
                        )}
                        {addon.period && <span className="text-xs text-gray-400 ml-1">{addon.period}</span>}
                      </>
                    ) : (
                      <span className="text-xl font-extrabold text-gray-900">Custom</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{addon.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Estimate Summary */}
      {(selected.size > 0 && (estimate.oneTime > 0 || estimate.monthly > 0)) && (
        <div className="max-w-3xl mx-auto mt-8 bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-blue-200 rounded-2xl p-6 sm:p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-600" /> Your Estimate
          </h3>
          <div className="space-y-2 text-sm">
            {selected.has('starter') && (
              <div className="flex justify-between">
                <span className="text-gray-600">Starter Package</span>
                <span className="font-bold">{coupon > 0 ? <><span className="text-red-600">${applyDiscount(starterPrice).toLocaleString()}</span> <span className="text-gray-400 line-through text-xs">${starterPrice.toLocaleString()}</span></> : `$${starterPrice.toLocaleString()}`}</span>
              </div>
            )}
            {selected.has('growth') && (
              <div className="flex justify-between">
                <span className="text-gray-600">Growth Package</span>
                <span className="font-bold">{coupon > 0 ? <><span className="text-red-600">${applyDiscount(growthPrice).toLocaleString()}</span> <span className="text-gray-400 line-through text-xs">${growthPrice.toLocaleString()}</span></> : `$${growthPrice.toLocaleString()}`}</span>
              </div>
            )}
            {allAddons.filter(a => selected.has(a.id) && a.price > 0).map(a => (
              <div key={a.id} className="flex justify-between">
                <span className="text-gray-600">{a.label} {a.period && <span className="text-gray-400 text-xs">({a.period.replace('/', '')})</span>}</span>
                <span className="font-bold">{coupon > 0 ? <><span className="text-red-600">${applyDiscount(a.price)}</span> <span className="text-gray-400 line-through text-xs">${a.price}</span></> : `$${a.price}`}</span>
              </div>
            ))}
            {coupon > 0 && (
              <div className="flex justify-between text-red-600 font-semibold border-t border-red-100 pt-2 mt-2">
                <span className="flex items-center gap-1"><Tag className="h-4 w-4" /> {coupon}% discount applied</span>
                <span>-${(estimate.oneTime - estimate.oneTimeDisc + estimate.monthly - estimate.monthlyDisc).toLocaleString()}</span>
              </div>
            )}
            <div className="border-t-2 border-blue-200 pt-3 mt-3">
              {estimate.oneTimeDisc > 0 && (
                <div className="flex justify-between text-lg font-extrabold text-gray-900">
                  <span>Total (one-time)</span>
                  <span>${estimate.oneTimeDisc.toLocaleString()}</span>
                </div>
              )}
              {estimate.monthlyDisc > 0 && (
                <div className="flex justify-between text-base font-bold text-gray-700 mt-1">
                  <span>+ Monthly</span>
                  <span>${estimate.monthlyDisc.toLocaleString()}/mo</span>
                </div>
              )}
            </div>
          </div>

          {/* Reserve Pricing */}
          {estimate.oneTimeDisc > 0 && (
            <div className="mt-6 bg-white border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-gray-900 text-sm">Reserve this pricing for 30 days</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Lock in {coupon > 0 ? 'your discounted rate' : 'today\'s pricing'} with a refundable ${deposit.toLocaleString()} deposit.
                    The deposit applies toward your project total. No obligation to continue.
                  </p>
                </div>
              </div>
            </div>
          )}

          <a
            href="#intake-form"
            className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-colors text-lg"
          >
            <Send className="h-5 w-5" />
            {estimate.oneTimeDisc > 0 ? `Get Started — $${deposit.toLocaleString()} Deposit` : 'Request Free Consultation'}
          </a>
          <p className="text-center text-xs text-gray-400 mt-2">Deposit is fully refundable. Full payment only after project completion &amp; your approval.</p>
        </div>
      )}

      {selected.size === 0 && (
        <p className="text-center text-sm text-gray-400 mt-6">Select a package above to see your estimate.</p>
      )}
    </div>
  );
};

export default WebsiteReportPage;
